import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { StyledEngineProvider } from '@mui/material';

import { PostHogProvider } from '@posthog/react';
import analytics from 'analytics/analytics';
import { ANALYTICS_APP } from 'analytics/constants';
import { watchFlags } from 'analytics/flagsStatus';
import 'assets/styles/index.scss';
import posthog from 'posthog-js';
import { PersistGate } from 'redux-persist/integration/react';
import { unregisterCurrentFcmToken } from 'services/fcmTokenStore';
import { firebaseConfig } from 'services/firebase/firebase';
import {
  canUseCookieFirstCategory,
  getCookieFirstConsent,
  initConsentCache,
  subscribeToConsent,
  type CookieFirstConsent,
} from 'utils/cookieFirst';

import App from './App';
import { store, persistor } from './redux/store';
import rollbar from './rollbarClient';

initConsentCache();

let isPostHogInitialized = false;

const initPostHog = () => {
  if (isPostHogInitialized) {
    return;
  }

  const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

  if (!posthogKey || !posthogHost) {
    return;
  }

  posthog.init(posthogKey, {
    api_host: posthogHost,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    opt_out_capturing_by_default: true,
    feature_flag_request_timeout_ms: 1500,
    advanced_disable_feature_flags_on_first_load: analytics.hasIdentity(),
  });
  posthog.register({ app: ANALYTICS_APP });
  watchFlags();

  isPostHogInitialized = true;
};

const syncPostHogConsent = (consent: CookieFirstConsent | null = getCookieFirstConsent()) => {
  if (!canUseCookieFirstCategory('performance', consent)) {
    if (isPostHogInitialized) {
      posthog.opt_out_capturing();
    }

    return;
  }

  initPostHog();

  if (isPostHogInitialized) {
    posthog.opt_in_capturing();
    analytics.replayIdentity();
  }
};

const setupPostHogConsentSync = () => {
  if (typeof window === 'undefined') {
    return;
  }

  syncPostHogConsent();
  subscribeToConsent(syncPostHogConsent);
};

setupPostHogConsentSync();

const container = document.getElementById('root');

if (!container) {
  throw new Error("Target container 'root' is not a DOM element.");
}

const root = ReactDOM.createRoot(container, {
  onRecoverableError: (error) => {
    rollbar.error(error as Error);
  },
});

root.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <Provider store={store}>
        <PersistGate persistor={persistor} loading={null}>
          <StyledEngineProvider injectFirst>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </StyledEngineProvider>
        </PersistGate>
      </Provider>
    </PostHogProvider>
  </React.StrictMode>
);

let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

const registerSW = (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  if (serviceWorkerRegistrationPromise) return serviceWorkerRegistrationPromise;

  const configParams = new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    appId: firebaseConfig.appId,
    messagingSenderId: firebaseConfig.messagingSenderId,
    projectId: firebaseConfig.projectId,
  });

  serviceWorkerRegistrationPromise = navigator.serviceWorker
    .register(`/sw.js?${configParams.toString()}`)
    .then((reg) => {
      // eslint-disable-next-line no-console
      console.log('Service Worker registrado:', reg);
      return reg;
    })
    .catch((err) => {
      serviceWorkerRegistrationPromise = null;
      // eslint-disable-next-line no-console
      console.error('Error registrando Service Worker:', err);
      return null;
    });

  return serviceWorkerRegistrationPromise;
};

const unregisterFcmTokenAfterFunctionalRevocation = (consent: CookieFirstConsent | null) => {
  if (!consent || consent.functional) return;

  try {
    if (localStorage.getItem('fcm_push_enabled') !== '0') {
      localStorage.setItem('fcm_push_enabled', '0');
    }
  } catch {
    // ignore
  }

  unregisterCurrentFcmToken().catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Error desregistrando token FCM tras revocar consentimiento funcional:', err);
  });
};

const setupServiceWorkerLifecycle = () => {
  if (typeof window === 'undefined') return;

  const syncFcmCleanup = (consent = getCookieFirstConsent()) => {
    unregisterFcmTokenAfterFunctionalRevocation(consent);
  };

  const initialConsent = getCookieFirstConsent();

  registerSW().catch(() => {});

  unregisterFcmTokenAfterFunctionalRevocation(initialConsent);
  subscribeToConsent(syncFcmCleanup);
};

setupServiceWorkerLifecycle();
